import { ContentOverrides } from './types';
import { setByPath } from './merge';
import { CONTENT_API_BASE, CONTENT_LOCAL_KEY } from '../siteConfig';

// A ContentStore loads the current override set and persists single-field
// edits. Two implementations share this seam: the browser-local store used
// for preview, and the remote DynamoDB-backed API used in production.

export interface SaveMeta {
  editor?: string;
  token?: string;
}

export interface HistoryEntry {
  path: string;
  value: unknown;
  editor: string;
  at: string;          // ISO timestamp
}

export interface ContentStore {
  readonly kind: 'local' | 'remote';
  load(): Promise<ContentOverrides>;
  saveField(path: string, value: unknown, meta?: SaveMeta): Promise<void>;
  clear(): Promise<void>;
  history(path: string): Promise<HistoryEntry[]>;
  recentAudit(): Promise<HistoryEntry[]>;
  uploadImage(dataUrl: string, filename: string, meta?: SaveMeta): Promise<string>;
}

// ── local: persists to this browser only ─────────────────────
export class LocalContentStore implements ContentStore {
  readonly kind = 'local' as const;

  async load(): Promise<ContentOverrides> {
    try {
      const raw = localStorage.getItem(CONTENT_LOCAL_KEY);
      return raw ? (JSON.parse(raw) as ContentOverrides) : {};
    } catch {
      return {};
    }
  }

  async saveField(path: string, value: unknown): Promise<void> {
    const cur = await this.load();
    const next = setByPath(cur as object, path, value);
    localStorage.setItem(CONTENT_LOCAL_KEY, JSON.stringify(next));
  }

  async clear(): Promise<void> {
    localStorage.removeItem(CONTENT_LOCAL_KEY);
  }

  async history(): Promise<HistoryEntry[]> { return []; }
  async recentAudit(): Promise<HistoryEntry[]> { return []; }

  // Local preview keeps the picked image inline as a data URL — no upload.
  async uploadImage(dataUrl: string): Promise<string> { return dataUrl; }
}

// ── remote: the DynamoDB-backed API (append-only history) ────
export class RemoteContentStore implements ContentStore {
  readonly kind = 'remote' as const;
  constructor(private base: string) {}

  async load(): Promise<ContentOverrides> {
    const res = await fetch(`${this.base}/content`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`content load failed: ${res.status}`);
    return res.json();
  }

  async saveField(path: string, value: unknown, meta?: SaveMeta): Promise<void> {
    const res = await fetch(`${this.base}/content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(meta?.token ? { Authorization: `Bearer ${meta.token}` } : {}),
      },
      body: JSON.stringify({ path, value }),
    });
    if (!res.ok) throw new Error(`content save failed: ${res.status}`);
  }

  async clear(): Promise<void> {
    // Server keeps history; there is no destructive clear from the client.
    throw new Error('remote store does not support clear');
  }

  async history(path: string): Promise<HistoryEntry[]> {
    const res = await fetch(`${this.base}/content/history?path=${encodeURIComponent(path)}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`history load failed: ${res.status}`);
    return res.json();
  }

  async recentAudit(): Promise<HistoryEntry[]> {
    const res = await fetch(`${this.base}/content/history`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`audit load failed: ${res.status}`);
    return res.json();
  }

  async uploadImage(dataUrl: string, filename: string, meta?: SaveMeta): Promise<string> {
    const res = await fetch(`${this.base}/content/image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(meta?.token ? { Authorization: `Bearer ${meta.token}` } : {}),
      },
      body: JSON.stringify({ filename, dataUrl }),
    });
    if (!res.ok) throw new Error(`image upload failed: ${res.status}`);
    const { url } = (await res.json()) as { url: string };
    return url;
  }
}

export function makeContentStore(): ContentStore {
  return CONTENT_API_BASE ? new RemoteContentStore(CONTENT_API_BASE) : new LocalContentStore();
}
